<template>
  <FormSubmitPopup
    :title="title"
    v-model="value"
    :rightMethod="onSubmitted"
    @popupClose="$emit('input', false)"
    :rightButtonText="actionText"
    large
  >
    <template v-slot:input>
      <v-layout wrap justify-start align-center>
        <v-flex sm2 ml-1 mr-1>
          <v-menu
            :close-on-content-click="false"
            v-model="endDatePicker"
            :nudge-right="40"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
            :disabled="loading"
          >
            <template v-slot:activator="data">
              <v-text-field
                v-model="endDateFormatted"
                :label="$t('endDate')"
                prepend-icon="event"
                readonly
                v-on="data.on"
                :rules="[
                  fieldIsRequired('Announcement end date'), 
                  value => (new Date(value) > endDateMinimum) || $t('endDateMustLargeThanToday')
                ]"
              ></v-text-field>
            </template>
            <v-date-picker
              :min="endDateMinimum.toISOString()"
              v-model="newAnnouncement.endDate"
              @input="endDatePicker = false"
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex sm3 v-if="isAdmin" ml-1 mr-1>
          <v-combobox
            :items="msgTypes"
            v-model="selectedMsgType"
            item-text="displayName"
            item-value="value"
            :label="$t('receivers')"
            :return-object="false"
            chips
            multiple
            clearable
          />
        </v-flex>
        <v-flex sm3 v-if="isAdmin || isWarehouse" ml-1 mr-1>
          <v-combobox
            :items="allPaths"
            v-model="selectedPaths"
            :label="$t('announcePath')"
            chips
            multiple
            clearable
          />
        </v-flex>
        <v-flex sm2 v-else-if="isOrganization" ml-1 mr-1>
          <v-checkbox
            :label="$t('sendEmail')"
            v-model="isSendOrganizationAnnouncementEmail"
          ></v-checkbox>
        </v-flex>
        <v-flex sm2 ml-4>
          <v-checkbox
            v-model="isImportant"
            :label="$t('important')"
          ></v-checkbox>
        </v-flex>
      </v-layout>
      <v-layout wrap justify-start align-center v-if="isWarehouse">
        <v-flex sm3 ml-1 mr-1>
          <v-combobox
            :items="userGroups"
            @change="changeUserGroup($event,selectedUserGroups)"
            v-model="selectedUserGroups"
            item-text="orgType"
            item-value="orgType"
            :label="$t('userGroups')"
            chips
            multiple
            clearable
          />
        </v-flex>
        <v-flex sm3 ml-1 mr-1>
          <v-combobox
            :items="organizations"
            v-model="selectedOrganizations"
            item-text="organizationId"
            item-value="organizationKey"
            :label="$t('organizations')"
            chips
            multiple
            clearable
          />
        </v-flex>
      </v-layout>
      <v-layout wrap justify-start align-center v-if="isWarehouse">
        <v-flex sm10>
          <b>{{ $t('uploadedFiles') }}: </b>
          <v-layout row wrap>
            <FsFileChip
            v-for="(file, index) in (attachments || [])"
            :file="file"
            :key="'file' + index"
            width="100%"
            aspect-ratio="1.7"
            class="ma-2"
          />
          </v-layout>
        </v-flex>
        <v-flex sm2>
          <UploadFileWidget
            v-model="attachments"
            :clearFiles="true"
            ref="fileUploader"
            type="comments"
            :maxFiles="9"
            :removeFiles="removeFiles"
            icon
            isUseCameraUpload
          />
        </v-flex>
      </v-layout>

      <v-layout pt-3 justify-center wrap>
        <v-textarea
          id="msgContent"
          :label="$t('announcement')"
          outline
          rows="20"
          :rules="[fieldIsRequired('Announcement content')]"
          v-model="newAnnouncement.msgContent"
          class="thinBox"
        ></v-textarea>
      </v-layout>
    </template>
  </FormSubmitPopup>
</template>

<script>
import FormSubmitPopup from './FormSubmitPopup'
import SelectWidget from './SelectWidget'
import { timeTools, checkRules } from '../utils/tools'
import UploadFileWidget from './UploadFileWidget.vue'
import FsFileChip from './FsFileChip'
export default {
  name: 'AnnouncementEdit',
  components: {
    FormSubmitPopup,
    SelectWidget,
    UploadFileWidget,
    FsFileChip
  },
  mixins: [timeTools, checkRules],
  data () {
    return {
      newAnnouncement: {},
      startDatePicker: false,
      endDatePicker: false,
      startDateFormatted: '',
      endDateFormatted: '',
      endDateMinimum: (new Date()),
      msgTypes: [
        { displayName: this.$t('warehouse'), chip: 'W', value: 'warehouse' }, 
        { displayName: this.$t('tenant'), chip: 'T', value: 'tenant' }, 
        { displayName: this.$t('user'), chip: 'U', value: 'user' }
      ],
      selectedMsgType: [],
      selectedPaths: [],
      isSendOrganizationAnnouncementEmail: false,
      loading: false,
      isImportant: false,
      selectedOrganizations: [],
      selectedUserGroups: [],
      attachments: [],
      removeFiles: 0
    }
  },
  mounted () {
    let newAnnouncement = ((this.announcement || {})._key && this.announcement.getData()) || {}
    if (this.isAdmin) {
      this.selectedMsgType = ['warehouse', 'tenant', 'user']
    }
    if (this.announcement._key) {
      newAnnouncement.startDate = newAnnouncement && this.toPickerDateString(newAnnouncement.startDate)
      newAnnouncement.endDate = newAnnouncement && this.toPickerDateString(newAnnouncement.endDate)
      newAnnouncement.msgContent = (newAnnouncement && newAnnouncement.msgContent) || ''
      newAnnouncement._key = this.announcement._key
      this.selectedMsgType = newAnnouncement.msgType
      this.selectedPaths = newAnnouncement.paths
      this.isImportant = this.announcement.isImportant
      this.selectedOrganizations = newAnnouncement.clients || []
      this.attachments = newAnnouncement.attachments || []
    } else {
      newAnnouncement.startDate = this.toPickerDateString(this.endDateMinimum)
    }
    this.newAnnouncement = newAnnouncement
  },
  watch: {
    'newAnnouncement.startDate': function (value) {
      this.startDateFormatted = this.formatDate(value)
    },
    'newAnnouncement.endDate': function (value) {
      this.endDateFormatted = this.formatDate(value)
    },
    immediate: true
  },
  computed: {
    isWarehouse () {
      return !!this.$store.getters.activeWarehouse
    },
    isAdmin () {
      return !!(this.$store.getters.user || {}).isAdmin
    },
    isOrganization () {
      return this.$store.getters.activeOrganization
    },
    members () {
      return this.$store.getters.users || []
    },
    allPaths () {
      return this.$router.options.routes.map(route => route.path).filter(path => path).sort((a, b) => a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    organizations () {
      return this.$store.getters.tenantsWithBillings || []
    },
    userGroups () {
      return this.organizations.reduce((arr, item) => {
        const orgType = this.getOrgType(item.features?.[0])
        const group = arr.find(g => g.orgType === orgType)
        if (group) {
          group.userGroups.push(item)
        } else {
          arr.push({ orgType, userGroups: [item] })
        }
        return arr
      }, []).sort((a, b) => a.orgType.localeCompare(b.orgType)) || []
    }
  },
  methods: {
    changeUserGroup (items) {
      const map = items.reduce((map, { userGroups }) => {
        userGroups.forEach(item => {
          map[item.key] = item
        })
        return map
      }, {})

      this.selectedOrganizations = this.selectedOrganizations.filter(org => {
        return map[org.key]
      })

      items.forEach(({ userGroups }) => {
        userGroups.forEach(item => {
          const { key } = item
          if (this.selectedOrganizations.some(org => org.key === key)) return
          this.selectedOrganizations.push(item)
        })
      })
    },
    getOrgType (feature) {
      let orgTypeMap = {
        InboundForecast: 'A',
        InboundForecastPkg: 'B',
        InboundForecastDropshipping: 'D'
      }
      return orgTypeMap[feature] || 'DEFAULT'
    },
    removeUploadFile (file) {
      return this.$store.dispatch('removeFile', file.fullPath)
        .then(() => {
          this.attachments = this.attachments.filter(({fullPath}) => file.fullPath !== fullPath)
          this.$refs.fileUploader.$$cleanData(this.attachments)
        })
    },
    onSubmitted () {
      this.loading = true
      let payload = {
        ...this.newAnnouncement, 
        msgType: this.selectedMsgType,
        startDate: this.toDateStart(this.newAnnouncement.startDate),
        endDate: this.toDateEnd(this.newAnnouncement.endDate),
        paths: this.selectedPaths || [],
        isImportant: this.isImportant || false,
        clients: this.selectedOrganizations.map(item => {
          return {
            key: item.key,
            organizationId: item.organizationId
          }
        }),
        attachments: this.attachments
      }
      return this.actionFunc(payload)
        .then(() => {
          if (this.isOrganization && this.isSendOrganizationAnnouncementEmail && this.members.length > 0) {
            const receivers = this.members.map(({email}) => email)
            return this.$store.dispatch('sendAnnouncementEmails', {
              msgContent: payload.msgContent,
              startDate: this.newAnnouncement.startDate,
              endDate: this.newAnnouncement.endDate,
              receivers
            })
          }
        })
        .finally(() => { this.loading = false })
    }
  },
  props: {
    title: String,
    value: Boolean,
    actionText: {
      type: String,
      default: 'Save'
    },
    actionFunc: Function,
    announcement: {
      type: Object,
      default: () => {
        return {}
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/AnnouncementEdit.vue